import {
	BufferAttribute,
	BufferGeometry,
	Color,
	ConeGeometry,
	HemisphereLight,
	LineBasicMaterial,
	LineSegments,
	Mesh,
	MeshStandardMaterial,
	RingGeometry,
	Scene,
	SphereGeometry
} from 'three';

import {
	getQualitySegments,
	HemisphereLightHelperObjects,
	LIGHT_HELPER_DEFAULTS,
	RenderQuality
} from './light-helper.types';

/** Default spacing between sky and ground spheres (vertical distance) */
const HEMISPHERE_AXIS_LENGTH = 40;

/** Number of gradient rings between sky and ground */
const GRADIENT_RING_COUNT = 5;

/** Size of the direction arrows */
const ARROW_HEIGHT = 6;
const ARROW_RADIUS = 2;

/** Control sphere radius */
const CONTROL_SPHERE_RADIUS = 3;

/** Ring thickness range based on intensity (min when intensity=0, max when intensity=max) */
const RING_THICKNESS_MIN = 0.5;
const RING_THICKNESS_MAX = 4.0;
const MAX_INTENSITY = 3.0; // Maximum expected intensity for scaling

/**
 * Interpolates between two colors
 * @param color1 First color
 * @param color2 Second color
 * @param t Interpolation factor (0 = color1, 1 = color2)
 * @returns Interpolated color
 */
const lerpColor = (color1: Color, color2: Color, t: number): Color => {
	const result = new Color();
	result.r = color1.r + (color2.r - color1.r) * t;
	result.g = color1.g + (color2.g - color1.g) * t;
	result.b = color1.b + (color2.b - color1.b) * t;
	return result;
};

/**
 * Calculates ring thickness based on light intensity
 */
const getRingThickness = (intensity: number): number => {
	const normalizedIntensity = Math.min(intensity / MAX_INTENSITY, 1);
	return RING_THICKNESS_MIN + (RING_THICKNESS_MAX - RING_THICKNESS_MIN) * normalizedIntensity;
};

/**
 * Creates visualization objects for a HemisphereLight
 *
 * Visualization includes:
 * - Sky sphere at top colored with sky color (interactive for color picking)
 * - Ground sphere at bottom colored with ground color (interactive for color picking)
 * - Control sphere at center (click toggles on/off, drag changes intensity)
 * - Vertical axis line connecting the two spheres
 * - 5 gradient rings showing the color blend (thickness represents intensity)
 * - Arrow pointing down from sky sphere
 * - Arrow pointing up from ground sphere
 *
 * @param light The HemisphereLight to visualize
 * @param scene Scene to add objects to
 * @param sphereRadius Radius of the spheres
 * @param visible Initial visibility state
 * @param quality Render quality 0-10
 * @returns HemisphereLight helper objects
 */
export const createHemisphereLightVisualization = (
	light: HemisphereLight,
	scene: Scene,
	sphereRadius: number = LIGHT_HELPER_DEFAULTS.CONE_RADIUS,
	visible: boolean = false,
	quality: RenderQuality = LIGHT_HELPER_DEFAULTS.QUALITY
): HemisphereLightHelperObjects => {
	const isLightOn = light.visible && light.intensity > 0;
	const segments = getQualitySegments(quality);

	// Get light colors
	const skyColor = light.color.clone();
	const groundColor = light.groundColor.clone();

	// Calculate positions - center the visualization at light position
	const centerY = light.position.y;
	const topY = centerY + HEMISPHERE_AXIS_LENGTH / 2;
	const bottomY = centerY - HEMISPHERE_AXIS_LENGTH / 2;

	// === SKY SPHERE (top) ===
	const skySphereGeometry = new SphereGeometry(sphereRadius, segments.radial, segments.radial);
	const skySphereMaterial = new MeshStandardMaterial({
		color: isLightOn ? skyColor : LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_OFF,
		wireframe: false, // Interface objects never wireframe
		emissive: isLightOn ? skyColor : new Color(0x000000),
		emissiveIntensity: isLightOn ? 0.3 : 0
	});
	const skySphere = new Mesh(skySphereGeometry, skySphereMaterial);
	skySphere.name = `hemisphereLightSkySphere_${light.uuid}`;
	skySphere.position.set(light.position.x, topY, light.position.z);
	skySphere.visible = visible;
	// Mark as interface object to exclude from wireframe mode
	skySphere.userData.isInterface = true;
	skySphere.userData.type = 'helper';
	scene.add(skySphere);

	// === GROUND SPHERE (bottom) ===
	const groundSphereGeometry = new SphereGeometry(sphereRadius, segments.radial, segments.radial);
	const groundSphereMaterial = new MeshStandardMaterial({
		color: isLightOn ? groundColor : LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_OFF,
		wireframe: false, // Interface objects never wireframe
		emissive: isLightOn ? groundColor : new Color(0x000000),
		emissiveIntensity: isLightOn ? 0.3 : 0
	});
	const groundSphere = new Mesh(groundSphereGeometry, groundSphereMaterial);
	groundSphere.name = `hemisphereLightGroundSphere_${light.uuid}`;
	groundSphere.position.set(light.position.x, bottomY, light.position.z);
	groundSphere.visible = visible;
	// Mark as interface object to exclude from wireframe mode
	groundSphere.userData.isInterface = true;
	groundSphere.userData.type = 'helper';
	scene.add(groundSphere);

	// === CONTROL SPHERE (center - for toggling and intensity) ===
	const middleColor = lerpColor(skyColor, groundColor, 0.5);
	const controlSphereGeometry = new SphereGeometry(CONTROL_SPHERE_RADIUS, segments.radial, segments.radial);
	const controlSphereMaterial = new MeshStandardMaterial({
		color: isLightOn ? middleColor : LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_OFF,
		wireframe: false, // Interface objects never wireframe
		transparent: true,
		opacity: 0.5,
		emissive: isLightOn ? middleColor : new Color(0x000000),
		emissiveIntensity: isLightOn ? 0.4 : 0
	});
	const controlSphere = new Mesh(controlSphereGeometry, controlSphereMaterial);
	controlSphere.name = `hemisphereLightControlSphere_${light.uuid}`;
	controlSphere.position.set(light.position.x, centerY, light.position.z);
	controlSphere.visible = visible;
	// Mark as interface object to exclude from wireframe mode
	controlSphere.userData.isInterface = true;
	controlSphere.userData.type = 'helper';
	scene.add(controlSphere);

	// === AXIS LINE (connecting line) ===
	const axisLineGeometry = new BufferGeometry();
	const axisLinePositions = new Float32Array([
		light.position.x, topY - sphereRadius, light.position.z,
		light.position.x, bottomY + sphereRadius, light.position.z
	]);
	axisLineGeometry.setAttribute('position', new BufferAttribute(axisLinePositions, 3));
	const axisLineMaterial = new LineBasicMaterial({
		color: 0xffffff,
		opacity: 0.6,
		transparent: true
	});
	const axisLine = new LineSegments(axisLineGeometry, axisLineMaterial);
	axisLine.name = `hemisphereLightAxisLine_${light.uuid}`;
	axisLine.visible = visible;
	// Mark as interface object to exclude from wireframe mode
	axisLine.userData.isInterface = true;
	axisLine.userData.type = 'helper';
	scene.add(axisLine);

	// === GRADIENT RINGS (thickness based on intensity) ===
	const gradientRings: Mesh[] = [];
	const ringSpacing = (HEMISPHERE_AXIS_LENGTH - sphereRadius * 2) / (GRADIENT_RING_COUNT + 1);
	const ringInnerRadius = sphereRadius * 1.2;
	const ringThickness = getRingThickness(light.intensity);

	for (let i = 0; i < GRADIENT_RING_COUNT; i++) {
		const t = (i + 1) / (GRADIENT_RING_COUNT + 1);
		const ringY = topY - sphereRadius - (i + 1) * ringSpacing;

		// Interpolate color from sky to ground
		const ringColor = lerpColor(skyColor, groundColor, t);

		const ringGeometry = new RingGeometry(
			ringInnerRadius,
			ringInnerRadius + ringThickness,
			segments.radial,
			1
		);
		const ringMaterial = new MeshStandardMaterial({
			color: isLightOn ? ringColor : LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_OFF,
			wireframe: false, // Interface objects never wireframe
			side: 2, // DoubleSide
			transparent: true,
			opacity: 0.7,
			emissive: isLightOn ? ringColor : new Color(0x000000),
			emissiveIntensity: isLightOn ? 0.2 : 0
		});
		const ring = new Mesh(ringGeometry, ringMaterial);
		ring.name = `hemisphereLightGradientRing_${i}_${light.uuid}`;
		ring.position.set(light.position.x, ringY, light.position.z);
		// Rotate ring to be horizontal (facing up/down)
		ring.rotation.x = -Math.PI / 2;
		ring.visible = visible;
		// Mark as interface object to exclude from wireframe mode
		ring.userData.isInterface = true;
		ring.userData.type = 'helper';
		scene.add(ring);
		gradientRings.push(ring);
	}

	// === SKY ARROW (pointing down from sky sphere) ===
	const skyArrowGeometry = new ConeGeometry(ARROW_RADIUS, ARROW_HEIGHT, segments.radial);
	const skyArrowMaterial = new MeshStandardMaterial({
		color: isLightOn ? skyColor : LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_OFF,
		wireframe: false, // Interface objects never wireframe
		emissive: isLightOn ? skyColor : new Color(0x000000),
		emissiveIntensity: isLightOn ? 0.3 : 0
	});
	const skyArrow = new Mesh(skyArrowGeometry, skyArrowMaterial);
	skyArrow.name = `hemisphereLightSkyArrow_${light.uuid}`;
	// Position below sky sphere, pointing down
	skyArrow.position.set(
		light.position.x,
		topY - sphereRadius - ARROW_HEIGHT / 2 - 1,
		light.position.z
	);
	// Rotate to point downward (cone points up by default)
	skyArrow.rotation.x = Math.PI;
	skyArrow.visible = visible;
	// Mark as interface object to exclude from wireframe mode
	skyArrow.userData.isInterface = true;
	skyArrow.userData.type = 'helper';
	scene.add(skyArrow);

	// === GROUND ARROW (pointing up from ground sphere) ===
	const groundArrowGeometry = new ConeGeometry(ARROW_RADIUS, ARROW_HEIGHT, segments.radial);
	const groundArrowMaterial = new MeshStandardMaterial({
		color: isLightOn ? groundColor : LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_OFF,
		wireframe: false, // Interface objects never wireframe
		emissive: isLightOn ? groundColor : new Color(0x000000),
		emissiveIntensity: isLightOn ? 0.3 : 0
	});
	const groundArrow = new Mesh(groundArrowGeometry, groundArrowMaterial);
	groundArrow.name = `hemisphereLightGroundArrow_${light.uuid}`;
	// Position above ground sphere, pointing up
	groundArrow.position.set(
		light.position.x,
		bottomY + sphereRadius + ARROW_HEIGHT / 2 + 1,
		light.position.z
	);
	// Cone already points up by default
	groundArrow.visible = visible;
	// Mark as interface object to exclude from wireframe mode
	groundArrow.userData.isInterface = true;
	groundArrow.userData.type = 'helper';
	scene.add(groundArrow);

	// Use skySphere as the primary sphere for compatibility with LightHelperObjectsBase
	return {
		sphere: skySphere, // Primary sphere for base interface compatibility
		skySphere,
		groundSphere,
		controlSphere,
		axisLine,
		gradientRings,
		skyArrow,
		groundArrow
	};
};

/**
 * Updates the gradient ring geometries based on intensity
 * Ring thickness represents the light intensity
 */
export const updateGradientRingThickness = (
	objects: HemisphereLightHelperObjects,
	intensity: number,
	sphereRadius: number,
	quality: RenderQuality
): void => {
	const segments = getQualitySegments(quality);
	const ringInnerRadius = sphereRadius * 1.2;
	const ringThickness = getRingThickness(intensity);

	for (const ring of objects.gradientRings) {
		// Dispose old geometry
		ring.geometry.dispose();

		// Create new geometry with updated thickness
		ring.geometry = new RingGeometry(
			ringInnerRadius,
			ringInnerRadius + ringThickness,
			segments.radial,
			1
		);
	}
};

/**
 * Updates HemisphereLight visualization objects
 */
export const updateHemisphereLightVisualization = (
	light: HemisphereLight,
	objects: HemisphereLightHelperObjects,
	visible: boolean
): void => {
	const isLightOn = light.visible && light.intensity > 0;
	const skyColor = light.color.clone();
	const groundColor = light.groundColor.clone();
	const middleColor = lerpColor(skyColor, groundColor, 0.5);

	// Calculate positions
	const centerY = light.position.y;
	const topY = centerY + HEMISPHERE_AXIS_LENGTH / 2;
	const bottomY = centerY - HEMISPHERE_AXIS_LENGTH / 2;
	const sphereRadius = (objects.skySphere.geometry as SphereGeometry).parameters.radius;

	// Update sky sphere
	objects.skySphere.position.set(light.position.x, topY, light.position.z);
	objects.skySphere.visible = visible;
	const skySphereMaterial = objects.skySphere.material as MeshStandardMaterial;
	skySphereMaterial.color.copy(isLightOn ? skyColor : new Color(LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_OFF));
	skySphereMaterial.emissive.copy(isLightOn ? skyColor : new Color(0x000000));
	skySphereMaterial.emissiveIntensity = isLightOn ? 0.3 : 0;

	// Update ground sphere
	objects.groundSphere.position.set(light.position.x, bottomY, light.position.z);
	objects.groundSphere.visible = visible;
	const groundSphereMaterial = objects.groundSphere.material as MeshStandardMaterial;
	groundSphereMaterial.color.copy(isLightOn ? groundColor : new Color(LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_OFF));
	groundSphereMaterial.emissive.copy(isLightOn ? groundColor : new Color(0x000000));
	groundSphereMaterial.emissiveIntensity = isLightOn ? 0.3 : 0;

	// Update control sphere
	objects.controlSphere.position.set(light.position.x, centerY, light.position.z);
	objects.controlSphere.visible = visible;
	const controlSphereMaterial = objects.controlSphere.material as MeshStandardMaterial;
	controlSphereMaterial.color.copy(isLightOn ? middleColor : new Color(LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_OFF));
	controlSphereMaterial.emissive.copy(isLightOn ? middleColor : new Color(0x000000));
	controlSphereMaterial.emissiveIntensity = isLightOn ? 0.4 : 0;
	// Adjust opacity based on light state
	controlSphereMaterial.opacity = isLightOn ? 0.5 : 0.3;

	// Update primary sphere reference (same as skySphere)
	objects.sphere.position.copy(objects.skySphere.position);
	objects.sphere.visible = visible;

	// Update axis line
	const axisLinePositions = objects.axisLine.geometry.attributes.position as BufferAttribute;
	axisLinePositions.setXYZ(0, light.position.x, topY - sphereRadius, light.position.z);
	axisLinePositions.setXYZ(1, light.position.x, bottomY + sphereRadius, light.position.z);
	axisLinePositions.needsUpdate = true;
	objects.axisLine.visible = visible;
	// Dim the axis line when light is off
	const axisLineMaterial = objects.axisLine.material as LineBasicMaterial;
	axisLineMaterial.opacity = isLightOn ? 0.6 : 0.2;

	// Update gradient rings
	const ringSpacing = (HEMISPHERE_AXIS_LENGTH - sphereRadius * 2) / (GRADIENT_RING_COUNT + 1);
	for (let i = 0; i < objects.gradientRings.length; i++) {
		const t = (i + 1) / (GRADIENT_RING_COUNT + 1);
		const ringY = topY - sphereRadius - (i + 1) * ringSpacing;
		const ringColor = lerpColor(skyColor, groundColor, t);

		const ring = objects.gradientRings[i];
		ring.position.set(light.position.x, ringY, light.position.z);
		ring.visible = visible;

		const ringMaterial = ring.material as MeshStandardMaterial;
		ringMaterial.color.copy(isLightOn ? ringColor : new Color(LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_OFF));
		ringMaterial.emissive.copy(isLightOn ? ringColor : new Color(0x000000));
		ringMaterial.emissiveIntensity = isLightOn ? 0.2 : 0;
		ringMaterial.opacity = isLightOn ? 0.7 : 0.3;
	}

	// Update sky arrow
	objects.skyArrow.position.set(
		light.position.x,
		topY - sphereRadius - ARROW_HEIGHT / 2 - 1,
		light.position.z
	);
	objects.skyArrow.visible = visible;
	const skyArrowMaterial = objects.skyArrow.material as MeshStandardMaterial;
	skyArrowMaterial.color.copy(isLightOn ? skyColor : new Color(LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_OFF));
	skyArrowMaterial.emissive.copy(isLightOn ? skyColor : new Color(0x000000));
	skyArrowMaterial.emissiveIntensity = isLightOn ? 0.3 : 0;

	// Update ground arrow
	objects.groundArrow.position.set(
		light.position.x,
		bottomY + sphereRadius + ARROW_HEIGHT / 2 + 1,
		light.position.z
	);
	objects.groundArrow.visible = visible;
	const groundArrowMaterial = objects.groundArrow.material as MeshStandardMaterial;
	groundArrowMaterial.color.copy(isLightOn ? groundColor : new Color(LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_OFF));
	groundArrowMaterial.emissive.copy(isLightOn ? groundColor : new Color(0x000000));
	groundArrowMaterial.emissiveIntensity = isLightOn ? 0.3 : 0;
};

/**
 * Disposes HemisphereLight visualization objects
 */
export const disposeHemisphereLightVisualization = (
	objects: HemisphereLightHelperObjects,
	scene: Scene
): void => {
	// Remove and dispose sky sphere
	scene.remove(objects.skySphere);
	objects.skySphere.geometry.dispose();
	(objects.skySphere.material as MeshStandardMaterial).dispose();

	// Remove and dispose ground sphere
	scene.remove(objects.groundSphere);
	objects.groundSphere.geometry.dispose();
	(objects.groundSphere.material as MeshStandardMaterial).dispose();

	// Remove and dispose control sphere
	scene.remove(objects.controlSphere);
	objects.controlSphere.geometry.dispose();
	(objects.controlSphere.material as MeshStandardMaterial).dispose();

	// Remove and dispose axis line
	scene.remove(objects.axisLine);
	objects.axisLine.geometry.dispose();
	(objects.axisLine.material as LineBasicMaterial).dispose();

	// Remove and dispose gradient rings
	for (const ring of objects.gradientRings) {
		scene.remove(ring);
		ring.geometry.dispose();
		(ring.material as MeshStandardMaterial).dispose();
	}

	// Remove and dispose sky arrow
	scene.remove(objects.skyArrow);
	objects.skyArrow.geometry.dispose();
	(objects.skyArrow.material as MeshStandardMaterial).dispose();

	// Remove and dispose ground arrow
	scene.remove(objects.groundArrow);
	objects.groundArrow.geometry.dispose();
	(objects.groundArrow.material as MeshStandardMaterial).dispose();
};
